// EB6 (register EB-CAN-06): the one-click re-auth door. Every stale-grant
// surface renders THIS for a re-auth-available refusal kind — one press opens
// the existing sign-in popup directly (the same ceremony the dock's login
// chip runs) and watches settlement; never navigation to a page with a
// button, never a terminal. Any other refusal kind renders nothing here,
// including a tool the World does not offer (see REAUTH_AVAILABLE_REFUSAL_KINDS).
import { isReauthAvailableRefusal } from "@cleo/contracts";
import { useEffect, useRef, useState, type ReactElement } from "react";

import { Button } from "./ui/button";
import {
  readStatusRefreshingWorlds,
  startNeucleosSignIn,
} from "./settings/NeucleosConnectionSettings";

export function reauthDoorLabel(kind: string): string {
  return kind === "not_signed_in" ? "Sign in" : "Sign in again";
}

export function ReauthDoor({
  kind,
  onSettled,
  className,
}: {
  readonly kind: string;
  /** Fires once when the watched sign-in settles (or the popup closes). */
  readonly onSettled?: (() => void) | undefined;
  readonly className?: string | undefined;
}): ReactElement | null {
  const [pending, setPending] = useState(false);
  const stop = useRef<(() => void) | null>(null);
  useEffect(() => () => stop.current?.(), []);
  if (!isReauthAvailableRefusal(kind)) return null;
  return (
    <Button
      className={className}
      data-neucleos-reauth-door={kind}
      disabled={pending}
      onClick={() => {
        void (async () => {
          setPending(true);
          try {
            stop.current?.();
            stop.current = await startNeucleosSignIn({
              read: readStatusRefreshingWorlds,
              onDone: () => {
                stop.current = null;
                onSettled?.();
              },
            });
          } finally {
            setPending(false);
          }
        })();
      }}
      size="sm"
      type="button"
    >
      {reauthDoorLabel(kind)}
    </Button>
  );
}
